/*
Copyright 2026 ВИН СОЛЮШЕНС

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/**
 * Своя картинка для фона: подготовка и хранение.
 *
 * Хранится она в IndexedDB, а не в настройках: настройки лежат в localStorage,
 * а это несколько мегабайт на всё про всё — один снимок с телефона туда просто
 * не влезет, и запись молча провалилась бы.
 *
 * Картинка не покидает устройство: на сервер её никто не отправляет, другим
 * участникам уходит уже готовый кадр.
 */

/** Длинная сторона, до которой ужимаем картинку перед сохранением. */
export const MAX_SIDE = 1920;

/** Сколько байт разрешаем хранить: выше этого картинка бесполезна, а память ест. */
export const MAX_STORED_BYTES = 8 * 1024 * 1024;

const DB_NAME = "klava-call-backgrounds";
const STORE = "images";
const KEY = "custom";

/** Можно ли вообще хранить картинку на этом устройстве. */
export function storageSupported(): boolean {
  return typeof indexedDB !== "undefined";
}

async function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = (): void => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = (): void => resolve(request.result);
    request.onerror = (): void => reject(request.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(STORE, mode);
    const request = run(transaction.objectStore(STORE));
    request.onsuccess = (): void => resolve(request.result);
    request.onerror = (): void => reject(request.error);
    transaction.oncomplete = (): void => db.close();
  });
}

/**
 * Ужимаем картинку до разумного размера и переводим в webp.
 *
 * Зачем: кадр всё равно рисуется размером с видео, а снимок на двенадцать
 * мегапикселей заняло бы место и время на каждом запуске звонка. Пропорции
 * сохраняем — обрезкой под кадр занимается сама библиотека.
 */
export async function prepareBackground(file: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = new OffscreenCanvas(width, height);
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Не удалось подготовить холст для картинки");
    context.drawImage(bitmap, 0, 0, width, height);

    return await canvas.convertToBlob({ type: "image/webp", quality: 0.85 });
  } finally {
    bitmap.close();
  }
}

/** Сохраняет картинку как свою. Возвращает то, что легло в хранилище. */
export async function saveCustomBackground(file: Blob): Promise<Blob> {
  const prepared = await prepareBackground(file);
  if (prepared.size > MAX_STORED_BYTES) {
    throw new Error("Картинка слишком большая даже после сжатия");
  }
  await withStore(
    "readwrite",
    (store) => store.put(prepared, KEY) as IDBRequest<IDBValidKey>,
  );
  return prepared;
}

/** Своя картинка или `null`, если её не выбирали. */
export async function loadCustomBackground(): Promise<Blob | null> {
  if (!storageSupported()) return null;
  try {
    const stored = await withStore<Blob | undefined>("readonly", (store) =>
      store.get(KEY),
    );
    return stored ?? null;
  } catch {
    // Хранилище может быть закрыто настройками браузера — это не поломка.
    return null;
  }
}

/** Забывает свою картинку. */
export async function clearCustomBackground(): Promise<void> {
  if (!storageSupported()) return;
  try {
    await withStore(
      "readwrite",
      (store) => store.delete(KEY) as IDBRequest<undefined>,
    );
  } catch {
    // Нечего было удалять либо хранилище недоступно.
  }
}
